/**
 * The interaction cone between a cell and its open preview card: a triangle
 * from where the pointer left the cell to the top and bottom of the card's
 * near edge. While the pointer travels through it the card holds, and rows it
 * crosses don't open over the card. Base UI's own safePolygon() closes the card
 * when the pointer slows, and lets crossed rows open.
 *
 * Leaving a cell is one of two gestures with opposite needs: reaching for the
 * card must hold the hover group from the instant the pointer leaves, and
 * dropping onto the next row must never hold it. Waiting can't tell them apart
 * in time, so the exit's direction decides; travel through the cone claims late
 * only for a trip that started ambiguously.
 *
 * Plain DOM, no React. `use-safe-triangle` wires it to a card.
 */

export interface Point {
  x: number
  y: number
}

type Rect = Pick<DOMRect, 'left' | 'right' | 'top' | 'bottom'>

export interface Cone {
  apex: Point
  /** Top of the card's near edge. */
  near: Point
  /** Bottom of the card's near edge. */
  far: Point
  /** 1 when the card is to the right of the exit, -1 when to its left. */
  towardCard: 1 | -1
}

/** How far behind the exit point the apex sits, so pointer jitter at the cell's edge stays inside. */
const APEX_BUFFER_PX = 4

/** How long the pointer may rest inside the cone before it reads as a change of mind. */
const REST_TIMEOUT_MS = 400

/**
 * How far toward the card the pointer must get for travel alone to claim. The
 * wedge is arbitrarily thin at the apex, so a pixel of drift onto the next row
 * is geometrically inside it.
 */
const COMMIT_TRAVEL_PX = 12

/** Ignore sub-pixel drift when reading the direction the pointer left in. */
const HEADING_NOISE_PX = 2

/**
 * How far back from the exit to read the direction. A slow pointer samples in
 * 1px steps, so the last step alone is too short to have a direction.
 */
const HEADING_WINDOW_PX = 10

/**
 * Samples older than this come before a pause. Samples only arrive while the
 * pointer moves, so the latest of them is where it rested before this motion.
 */
const HEADING_WINDOW_MS = 150

/** Null when the card sits over or under the exit point rather than beside it. */
export function getSafeTriangle(exit: Point, card: Rect): Cone | null {
  if (exit.x >= card.left && exit.x <= card.right) {
    return null
  }
  const towardCard = exit.x < card.left ? 1 : -1
  const nearX = towardCard === 1 ? card.left : card.right
  return {
    apex: { x: exit.x - towardCard * APEX_BUFFER_PX, y: exit.y },
    near: { x: nearX, y: card.top },
    far: { x: nearX, y: card.bottom },
    towardCard,
  }
}

export function isInSafeTriangle(point: Point, { apex, near, far }: Cone): boolean {
  const cross = (p: Point, q: Point, r: Point) => (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x)
  const d1 = cross(apex, near, point)
  const d2 = cross(near, far, point)
  const d3 = cross(far, apex, point)
  const hasNegative = d1 < 0 || d2 < 0 || d3 < 0
  const hasPositive = d1 > 0 || d2 > 0 || d3 > 0
  return !(hasNegative && hasPositive)
}

/**
 * The cone's viewport bounding box, which the bridge covers. Includes the apex:
 * collision shifting can leave the exit above or below the card.
 */
export function getConeBounds({ apex, near, far }: Cone) {
  const top = Math.min(apex.y, near.y, far.y)
  return {
    left: Math.min(apex.x, near.x),
    top,
    width: Math.abs(apex.x - near.x),
    height: Math.max(apex.y, near.y, far.y) - top,
  }
}

/** Horizontal ground gained on the card moving from `from` to `to`. */
function progressTowardCard(from: Point, to: Point, cone: Cone): number {
  return (to.x - from.x) * cone.towardCard
}

/** Whether a move gains any real ground on the card. Enough to bridge the gap, not to claim. */
export function leansTowardCard(from: Point, to: Point, cone: Cone): boolean {
  return progressTowardCard(from, to, cone) >= HEADING_NOISE_PX
}

/**
 * Whether a move is a reach for the card rather than a drop onto a neighbouring
 * row. Reaching sideways is mostly horizontal and moving down the list is mostly
 * vertical, so the move must run at least 45 degrees toward the card.
 */
export function isHeadingToCard(from: Point, to: Point, cone: Cone): boolean {
  return leansTowardCard(from, to, cone) && progressTowardCard(from, to, cone) >= Math.abs(to.y - from.y)
}

export interface PointerSample extends Point {
  /** `performance.now()` when the sample was taken. */
  at: number
}

/**
 * The point to read the exit's direction from: the most recent sample at least
 * HEADING_WINDOW_PX back. A motion shorter than that started where the pointer
 * last rested, so the walk stops at the first sample from before the pause.
 */
export function getHeadingOrigin(samples: ReadonlyArray<PointerSample>, exit: Point, now: number): Point | null {
  let origin: Point | null = null
  for (let i = samples.length - 1; i >= 0; i--) {
    const sample = samples[i]
    origin = sample
    const rested = now - sample.at > HEADING_WINDOW_MS
    if (rested || Math.hypot(sample.x - exit.x, sample.y - exit.y) >= HEADING_WINDOW_PX) break
  }
  return origin
}

/**
 * Whether the pointer left the cell through its card-facing edge. Only the gap
 * and the card lie that way, so there is no neighbouring row to tell it from.
 */
function leftTowardCardSide(exit: Point, cell: Rect, cone: Cone): boolean {
  const edge = cone.towardCard === 1 ? cell.right : cell.left
  return (exit.x - edge) * cone.towardCard >= 0
}

export function isInRect(point: Point, rect: Rect): boolean {
  return point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom
}

/**
 * How a trip ended. `landed`: reached the card or came back to the cell.
 * `fizzled`: never committed, so nothing was held. `abandoned`: committed, then
 * left the cone or rested in it. `at` is where the pointer was then.
 */
export type TripEnd = { how: 'landed' } | { how: 'fizzled' } | { how: 'abandoned'; at: Point }

interface ConeTrip {
  cone: Cone
  exit: Point
  /** Where the pointer was shortly before the exit, which turns the exit into a direction. */
  from: Point | null
  popup: HTMLElement
  trigger: HTMLElement
  /**
   * The trip may be a reach, so the gap needs bridging to report moves. Fires at
   * most once: at the exit when it leans toward the card, otherwise on claim.
   */
  onBridge: () => void
  /** The pointer committed to the trip. Fires at most once. */
  onClaim: () => void
  /** Fires once, after which the trip has detached itself. */
  onEnd: (end: TripEnd) => void
}

/**
 * Follows the pointer from a cell's exit until the trip ends. Returns a stop
 * function that detaches without reporting, for a trip cut short from outside.
 */
export function trackConeTrip({ cone, exit, from, popup, trigger, onBridge, onClaim, onEnd }: ConeTrip): () => void {
  let bridged = false
  let claimed = false
  let lastPoint = exit
  let restTimer: ReturnType<typeof setTimeout> | undefined

  const bridge = () => {
    if (bridged) return
    bridged = true
    onBridge()
  }

  const claim = () => {
    if (claimed) return
    claimed = true
    bridge()
    onClaim()
  }

  const handleMove = (move: PointerEvent) => {
    lastPoint = { x: move.clientX, y: move.clientY }
    const target = move.target instanceof Node ? move.target : null
    if (target && (popup.contains(target) || trigger.contains(target))) {
      end({ how: 'landed' })
      return
    }
    // Inside the card's box but not on the card: a rounded corner, which
    // hit-testing skips and the cone's far vertices point straight at. Base UI
    // only counts a move that targets the card as arrival, so keep holding until
    // one does.
    if (isInRect(lastPoint, popup.getBoundingClientRect())) {
      restartRestTimer()
      claim()
      return
    }
    if (!isInSafeTriangle(lastPoint, cone)) {
      leaveCone()
      return
    }
    restartRestTimer()
    if (progressTowardCard(cone.apex, lastPoint, cone) >= COMMIT_TRAVEL_PX) {
      claim()
    }
  }

  const stop = () => {
    clearTimeout(restTimer)
    document.removeEventListener('pointermove', handleMove, true)
  }

  const end = (trip: TripEnd) => {
    stop()
    onEnd(trip)
  }

  const leaveCone = () => end(claimed ? { how: 'abandoned', at: lastPoint } : { how: 'fizzled' })

  const restartRestTimer = () => {
    clearTimeout(restTimer)
    restTimer = setTimeout(leaveCone, REST_TIMEOUT_MS)
  }

  document.addEventListener('pointermove', handleMove, true)
  restartRestTimer()
  if (leftTowardCardSide(exit, trigger.getBoundingClientRect(), cone) || (from && isHeadingToCard(from, exit, cone))) {
    claim()
  } else if (from && leansTowardCard(from, exit, cone)) {
    bridge()
  }
  return stop
}
